import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { loadContent, saveDoc, type DocKey } from "../lib/contentStore";
import { api } from "../lib/api";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { TransButtons } from "../components/TransButtons";

/** 「有专门菜单维护」的区域 → 对应后台菜单（右键这些区域给跳转提示，不就地编辑） */
const REDIRECT_TARGETS: Record<string, {
  label: string;
  to: string;
  desc: string;
  idLabel?: string;
  // 带 id 跳转时如何拼具体明细页的路径；不传则用 `${to}?edit=<id>`（如动态新闻列表页消费 ?edit）
  idTo?: (id: string) => string;
}> = {
  jobs: { label: "招聘信息", to: "/jobs", desc: "招聘职位列表（职位名称、地点、要求等）由「招聘信息」菜单单独维护。" },
  news: { label: "动态新闻", to: "/news", desc: "动态文章（标题、配图、正文排版）由「动态新闻」菜单单独维护。", idLabel: "打开这篇文章编辑 →" },
  "studio-reports": { label: "智库报告", to: "/studio/reports", desc: "公开展示区的报告卡片（标题、链接、可见性）由「智库报告」菜单单独维护。" },
  projects: {
    label: "投资项目",
    to: "/projects",
    desc: "公司名称、股票代码、Logo 等由「投资项目」菜单的成员维护，这里仅展示，不在文案编辑里修改。",
    idLabel: "打开这家公司编辑 →",
    idTo: (id) => `/projects/${id}`,
  },
};

/**
 * 「实时预览 + 旁边编辑」页面文案编辑器。
 *
 * 左侧 iframe 内嵌官网真实页面（带 ?edit=1 进入内联编辑模式，见
 * apps/web/src/90-/edit-mode.jsx）。点击页面上任一段文字，官网经
 * postMessage 上报内容路径；右侧据此显示该文字的中/英字段，编辑即时
 * 回传 iframe 刷新预览，保存写库。
 *
 * 复用官网真实样式，无需在后台重写页面，也不需长期维护镜像。
 */

// 官网地址（iframe 预览指向公开站），优先级：
//  1) 构建时注入的 VITE_WEB_BASE（生产：caddy.Dockerfile 从 .env 的 SITE_DOMAIN 传入 build arg）；
//  2) window.__KEXI_WEB_BASE 运行时覆盖；
//  3) dev 回退：当前主机名 + 5173（vite，兼容局域网访问）。
const WEB_BASE =
  (import.meta.env.VITE_WEB_BASE as string | undefined) ||
  (window as any).__KEXI_WEB_BASE ||
  `${location.protocol}//${location.hostname}:5173`;

type Picked = {
  path: string;
  doc: DocKey;
  /** 中文值路径（不可双语时即唯一路径） */
  cnPath: (string | number)[];
  /** 英文值路径（无则 null） */
  enPath: (string | number)[] | null;
  ambiguous: boolean;
  /** 语言中性字段（装饰序号等）：只显示一个输入框，编辑同步写中英 */
  neutral?: boolean;
  /** 字段类型：text（默认）| image（上传 + 宽高适配） */
  kind?: "text" | "image";
  /** image 类型：指向 {url,fit} 对象的路径 */
  objPath?: (string | number)[];
  /** 关联链接字段（如「查看小站」按钮）：面板额外显示 URL 输入框 */
  linkPath?: (string | number)[] | null;
};

/** 把 raw 路径（site_data.x.y）解析为 doc + 段数组（数字段转 number） */
function rawToObjPath(raw: string): { doc: DocKey; objPath: (string | number)[] } | null {
  const parts = raw.split(".");
  const doc = parts[0] as DocKey;
  if (doc !== "site_data" && doc !== "i18n") return null;
  const objPath = parts.slice(1).map((p) => (/^\d+$/.test(p) ? Number(p) : p));
  return { doc, objPath };
}

function parsePath(raw: string): Picked | null {
  const parts = raw.split(".");
  const doc = parts[0] as string;
  if (doc !== "site_data" && doc !== "i18n") return null;
  const rest = parts.slice(1).map((p) => (/^\d+$/.test(p) ? Number(p) : p));

  if (doc === "i18n") {
    // i18n.<lang>.<ns>.<key>
    const [, ...kv] = rest;
    const cnPath = ["cn", ...kv];
    const enPath = ["en", ...kv];
    return { path: raw, doc, cnPath, enPath, ambiguous: false } as Picked;
  }
  // site_data：路径中任意一段是 cn/en 即视为双语对
  // （末段 cn/en，如 brand.fullCn 的 .cn；或中间 cn/en，如 i18nStudio.cn.uploadCta）
  const langIdx = rest.findIndex((p) => p === "cn" || p === "en");
  if (langIdx >= 0) {
    const cnPath = rest.map((p, i) => (i === langIdx ? "cn" : p));
    const enPath = rest.map((p, i) => (i === langIdx ? "en" : p));
    return { path: raw, doc, cnPath, enPath, ambiguous: false };
  }
  return { path: raw, doc, cnPath: rest, enPath: null, ambiguous: false };
}

function getAt(node: any, path: (string | number)[]): any {
  return path.reduce((n, k) => (n == null ? n : n[k]), node);
}

/** 深扫一个值里所有图片字段的 url（site_data 里图片统一是 {url,fit} 形态）。删整项时收集待删文件用。 */
function collectUrls(node: any, out: string[] = []): string[] {
  if (!node || typeof node !== "object") return out;
  if (Array.isArray(node)) { for (const v of node) collectUrls(v, out); return out; }
  for (const [k, v] of Object.entries(node)) {
    if (k === "url" && typeof v === "string" && v) out.push(v);
    else if (v && typeof v === "object") collectUrls(v, out);
  }
  return out;
}

function setAtPath(node: any, path: (string | number)[], value: any): any {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  if (Array.isArray(node)) {
    const copy = node.slice();
    copy[head as number] = setAtPath(node[head as number], rest, value);
    return copy;
  }
  return { ...(node ?? {}), [head]: setAtPath(node?.[head as string], rest, value) };
}

function sizeRows(...texts: string[]) {
  const max = Math.max(...texts.map((t) => t?.length ?? 0), 0);
  const multiline = texts.some((t) => t?.includes("\n"));
  if (max <= 42 && !multiline) return 0; // 0 → 单行 input
  return Math.min(10, Math.max(3, Math.ceil(max / 40)));
}

type Props = {
  /** 官网路由：home / about / strategy / studio / bp / contact 等 */
  route: string;
  title: string;
  subtitle?: string;
};

export function LivePreviewEditor({ route, title, subtitle }: Props) {
  // 注意：不读 peekContent 的草稿缓存——LivePreviewEditor 的未保存改动只留在本组件
  // state，不写回共享缓存，避免一个页面没保存就切走、却污染别的页面预览。
  const nav = useNavigate();
  const [params] = useSearchParams();
  // 跳转返回时定位：进入时若带 ?webroute=...，iframe 初始就停在该页
  const initialWebRoute = params.get("webroute") || route;
  // iframe 当前停留的官网路由（随内部导航更新；跳到子菜单编辑时一并带走，便于返回定位）
  const currentRoute = useRef(initialWebRoute);
  const [docs, setDocs] = useState<Record<DocKey, any> | null>(null);
  const [picked, setPicked] = useState<Picked | null>(null);
  const [redirect, setRedirect] = useState<{ target: string; id: string | null } | null>(null);
  const [dirty, setDirty] = useState<Record<DocKey, boolean>>({ site_data: false, i18n: false });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [iframeReady, setIframeReady] = useState(false);
  // 待确认的删除（用应用内弹窗替代不可靠的 window.confirm）
  const [pendingDelete, setPendingDelete] = useState<{ list: string; index: number } | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const iframeSrc = useMemo(
    () => `${WEB_BASE}/${initialWebRoute === "home" ? "" : initialWebRoute}?edit=1`,
    // 仅首挂载时计算一次，避免 query 变化重载 iframe
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // 每次进入都从已保存的干净数据加载（loadContent 缓存的是写库成功的内容）
  useEffect(() => {
    loadContent()
      .then(({ docs: d }) => setDocs(d))
      .catch((e) => setError(String(e)));
  }, []);

  const post = (msg: any) => {
    iframeRef.current?.contentWindow?.postMessage({ source: "kexi-admin", ...msg }, "*");
  };

  // 新增项模板
  const LIST_TEMPLATE: Record<string, () => any> = {
    timeline: () => ({ yr: String(new Date().getFullYear()), titleCn: "新里程碑", titleEn: "New milestone", body: { cn: "请填写内容…", en: "Edit me…" } }),
    team: () => ({ cn: "新成员", en: "New member", role: { cn: "职位", en: "Role" }, bio: { cn: "简介…", en: "Bio…" }, glyph: "新", photo: { url: "", fit: "width" } }),
  };

  // 列表增删 / 移动（site_data 下的数组），改后整体回写预览（数组整体替换重渲染）
  const applyListOp = (list: string, op: string, index: number) => {
    // 删除：弹应用内确认窗口（window.confirm 可能被浏览器永久屏蔽，不可靠）
    if (op === "delete") { setPendingDelete({ list, index }); return; }
    commitListOp(list, op, index);
  };
  const commitListOp = (list: string, op: string, index: number) => {
    setDocs((prev) => {
      if (!prev) return prev;
      const arr: any[] = Array.isArray(prev.site_data?.[list]) ? prev.site_data[list].slice() : [];
      if (op === "delete") {
        if (index < 0 || index >= arr.length) return prev;
        // 删整项前，把该项内所有图片一并删除（如团队成员的 photo.url）。
        // 全库复查，仍被已保存版本引用则后端保留，不误删。
        for (const u of collectUrls(arr[index])) api.dropFile(u);
        arr.splice(index, 1);
      } else if (op === "move-up") {
        if (index <= 0) return prev;
        [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
      } else if (op === "move-down") {
        if (index >= arr.length - 1) return prev;
        [arr[index + 1], arr[index]] = [arr[index], arr[index + 1]];
      } else if (op === "add") {
        const tpl = LIST_TEMPLATE[list];
        if (!tpl) return prev;
        arr.push(tpl());
      } else return prev;
      const nextSite = { ...prev.site_data, [list]: arr };
      const next = { ...prev, site_data: nextSite };
      post({ type: "update", site_data: nextSite });
      return next;
    });
    setDirty((p) => ({ ...p, site_data: true }));
    setSavedAt(null);
    setPicked(null);
  };

  // 接收官网上报（语言切换在 iframe 内部完成，后台不再提供切换按钮）
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const m = e.data;
      if (!m || m.source !== "kexi-edit") return;
      if (m.type === "ready") {
        setIframeReady(true);
      } else if (m.type === "route") {
        if (m.route) currentRoute.current = m.route;
      } else if (m.type === "list-op") {
        applyListOp(m.list, m.op, m.index);
      } else if (m.type === "pick" && m.kind === "image") {
        const r = rawToObjPath(m.path);
        if (r) {
          setRedirect(null);
          setPicked({ path: m.path, doc: r.doc, cnPath: r.objPath, enPath: null, ambiguous: false, kind: "image", objPath: r.objPath });
        }
      } else if (m.type === "pick") {
        const p = parsePath(m.path);
        if (p) {
          p.ambiguous = !!m.ambiguous; p.neutral = !!m.neutral;
          if (m.linkPath) { const lr = rawToObjPath(m.linkPath); if (lr) p.linkPath = lr.objPath; }
          setRedirect(null); setPicked(p);
        }
      } else if (m.type === "pick-redirect") {
        if (REDIRECT_TARGETS[m.target]) { setPicked(null); setRedirect({ target: m.target, id: m.targetId ?? null }); }
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // 每处文字按唯一字段单独修改（不做多处同步）。
  // 语言中性字段（neutral）：一个输入框，cn/en 两路同步写入，保持一致。
  const editField = (which: "cn" | "en", value: string) => {
    if (!picked || !docs) return;
    const paths: (string | number)[][] = picked.neutral
      ? [picked.cnPath, ...(picked.enPath ? [picked.enPath] : [])]
      : [(which === "cn" ? picked.cnPath : picked.enPath) ?? []];
    if (paths.length === 0 || paths[0].length === 0) return;
    const doc = picked.doc;
    setDocs((prev) => {
      if (!prev) return prev;
      let nextDoc = prev[doc];
      for (const path of paths) nextDoc = setAtPath(nextDoc, path, value);
      const next = { ...prev, [doc]: nextDoc };
      post({ type: "update", [doc]: nextDoc });
      return next;
    });
    setDirty((prev) => ({ ...prev, [doc]: true }));
    setSavedAt(null);
  };

  // 图片字段：写入 {url,fit} 对象的某个键
  const editImage = (key: "url" | "fit", value: string) => {
    if (!picked || !picked.objPath || !docs) return;
    const doc = picked.doc;
    setDocs((prev) => {
      if (!prev) return prev;
      const nextDoc = setAtPath(prev[doc], [...picked.objPath!, key], value);
      const next = { ...prev, [doc]: nextDoc };
      post({ type: "update", [doc]: nextDoc });
      return next;
    });
    setDirty((prev) => ({ ...prev, [doc]: true }));
    setSavedAt(null);
  };
  // 关联链接字段写入
  const editLink = (value: string) => {
    if (!picked || !picked.linkPath || !docs) return;
    const doc = picked.doc;
    setDocs((prev) => {
      if (!prev) return prev;
      const nextDoc = setAtPath(prev[doc], picked.linkPath!, value);
      const next = { ...prev, [doc]: nextDoc };
      post({ type: "update", [doc]: nextDoc });
      return next;
    });
    setDirty((prev) => ({ ...prev, [doc]: true }));
    setSavedAt(null);
  };
  const [imgUploading, setImgUploading] = useState(false);
  const uploadImage = async (file: File) => {
    setImgUploading(true);
    // 换图前先记下旧图 url，上传成功后删旧图（全库复查，仍被已保存版本引用则后端保留）
    const prev = picked?.objPath ? getAt(docs?.[picked.doc], [...picked.objPath, "url"]) : null;
    try {
      const res = await api.upload<{ url: string }>("/api/admin/uploads", file, "site/");
      editImage("url", res.url);
      api.dropFile(prev);
    } catch (e) {
      setError(`上传失败：${String(e)}`);
    } finally { setImgUploading(false); }
  };
  // 移除图片：清空 url 并删对象存储文件（全库复查，仍被引用则保留，不误删已保存的图）
  const removeImage = () => {
    if (!picked?.objPath) return;
    const u = getAt(docs?.[picked.doc], [...picked.objPath, "url"]);
    editImage("url", "");
    api.dropFile(u);
  };

  const save = async () => {
    if (!docs) return;
    setSaving(true);
    setError(null);
    try {
      const keys: DocKey[] = ["site_data", "i18n"];
      const savedPayload: any = { type: "update" };
      for (const k of keys) {
        if (dirty[k]) { await saveDoc(k, docs[k]); savedPayload[k] = docs[k]; }
      }
      setDirty({ site_data: false, i18n: false });
      setSavedAt(new Date().toLocaleTimeString("zh-CN"));
      // 保存后主动把已保存内容推给预览，避免 iframe 若重载命中旧缓存导致回退
      post(savedPayload);
    } catch (e) {
      setError(`保存失败：${String(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const anyDirty = dirty.site_data || dirty.i18n;
  const cnVal = picked && docs ? (getAt(docs[picked.doc], picked.cnPath) ?? "") : "";
  const enVal = picked && docs && picked.enPath ? (getAt(docs[picked.doc], picked.enPath) ?? "") : "";
  const rows = sizeRows(String(cnVal), String(enVal));

  return (
    <div className="lpe-page">
      <div className="page-header">
        <div>
          <div className="page-title">{title}</div>
          {subtitle && <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>{subtitle}</div>}
        </div>
        <div className="page-actions" style={{ alignItems: "center", gap: 16 }}>
          {savedAt && !anyDirty && <span className="success" style={{ fontSize: 12 }}>已保存 {savedAt}</span>}
          {anyDirty && <span className="ce-dirty">有未保存修改</span>}
          <button className="btn btn-primary btn-save" disabled={!anyDirty || saving} onClick={save}>
            {saving ? "保存中…" : "保 存"}
          </button>
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="lpe-body">
        <div className="lpe-preview">
          {!iframeReady && <div className="lpe-loading muted">预览加载中…</div>}
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            title="官网预览"
            className="lpe-iframe"
          />
        </div>

        <aside className="lpe-panel">
          {redirect && REDIRECT_TARGETS[redirect.target] ? (
            <div className="lpe-hint">
              <div className="lpe-hint-icon">↗</div>
              <p>这里是<b>{REDIRECT_TARGETS[redirect.target].label}</b>区域，不在文案编辑里修改。</p>
              <p className="muted" style={{ fontSize: 13, lineHeight: 1.6 }}>{REDIRECT_TARGETS[redirect.target].desc}</p>
              <button className="btn btn-primary" style={{ marginTop: 16 }}
                onClick={() => {
                  const tgt = REDIRECT_TARGETS[redirect.target];
                  const wr = encodeURIComponent(currentRoute.current);
                  let url: string;
                  if (redirect.id) {
                    const base = tgt.idTo ? tgt.idTo(redirect.id) : `${tgt.to}?edit=${redirect.id}`;
                    url = `${base}${base.includes("?") ? "&" : "?"}from=pages&webroute=${wr}`;
                  } else {
                    url = `${tgt.to}?from=pages&webroute=${wr}`;
                  }
                  nav(url);
                }}>
                {redirect.id
                  ? (REDIRECT_TARGETS[redirect.target].idLabel || `打开编辑 →`)
                  : `前往「${REDIRECT_TARGETS[redirect.target].label}」编辑 →`}
              </button>
            </div>
          ) : !picked ? (
            <div className="lpe-hint">
              <div className="lpe-hint-icon">←</div>
              <p>在左侧官网预览中，<b>右键任意一段文字</b>即可在此编辑它的中英文内容。</p>
              <p className="muted" style={{ fontSize: 12 }}>悬停时文字会高亮；保存后约 1 分钟内对访客生效。</p>
            </div>
          ) : picked.kind === "image" ? (
            (() => {
              const imgObj = docs ? getAt(docs[picked.doc], picked.objPath!) : null;
              const url = imgObj?.url || "";
              const fit = imgObj?.fit || "width";
              const src = url ? (/^https?:\/\//.test(url) || url.startsWith("/") ? url : `/api/files/${url}`) : "";
              return (
                <div className="lpe-fields">
                  <div className="lpe-field-head">
                    <span className="lpe-field-tag">图片</span>
                    <code className="lpe-path">{picked.path}</code>
                  </div>
                  <div className="lpe-field">
                    <div className="ce-label">当前图片</div>
                    {src
                      ? <div className="ne-img-preview"><img src={src} alt="" style={{ maxWidth: "100%" }} /></div>
                      : <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>暂未上传（显示占位）</div>}
                    <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                      <label className="btn" style={{ cursor: "pointer" }}>
                        {imgUploading ? "上传中…" : (url ? "更换图片" : "上传图片")}
                        <input type="file" accept="image/*" style={{ display: "none" }}
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f); e.target.value = ""; }} />
                      </label>
                      {url && <button className="btn" onClick={removeImage}>移除</button>}
                    </div>
                  </div>
                  <div className="lpe-field">
                    <div className="ce-label">适配方式</div>
                    <select className="ce-input" value={fit} onChange={(e) => editImage("fit", e.target.value)}>
                      <option value="width">适配宽度（按原比例铺满宽度）</option>
                      <option value="height">适配高度（撑满高度，超出裁切）</option>
                    </select>
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="lpe-fields">
              <div className="lpe-field-head">
                <span className="lpe-field-tag">已选中</span>
                <code className="lpe-path">{picked.path}</code>
              </div>
              <div className="lpe-field">
                <div className="ce-label">{picked.neutral ? "内容（不区分语言）" : "中文"}</div>
                {rows ? (
                  <textarea className="ce-input" rows={rows} value={String(cnVal)} onChange={(e) => editField("cn", e.target.value)} />
                ) : (
                  <input className="ce-input" value={String(cnVal)} onChange={(e) => editField("cn", e.target.value)} />
                )}
              </div>
              {!picked.neutral && picked.enPath && (
                <div className="lpe-trans-row">
                  <TransButtons vertical
                    getCn={() => String(cnVal)} getEn={() => String(enVal)}
                    setCn={(v) => editField("cn", v)} setEn={(v) => editField("en", v)} />
                </div>
              )}
              {!picked.neutral && picked.enPath && (
                <div className="lpe-field">
                  <div className="ce-label">English</div>
                  {rows ? (
                    <textarea className="ce-input" rows={rows} value={String(enVal)} onChange={(e) => editField("en", e.target.value)} />
                  ) : (
                    <input className="ce-input" value={String(enVal)} onChange={(e) => editField("en", e.target.value)} />
                  )}
                </div>
              )}
              {(!picked.enPath || picked.neutral) && (
                <div className="muted" style={{ fontSize: 12 }}>该字段不区分语言（如编号、代码），中英共用同一内容。</div>
              )}
              {picked.linkPath && docs && (
                <div className="lpe-field" style={{ marginTop: 16, borderTop: "1px solid var(--line-2)", paddingTop: 16 }}>
                  <div className="ce-label">链接 URL（点击按钮跳转的地址）</div>
                  <input className="ce-input" placeholder="https://..."
                    value={String(getAt(docs[picked.doc], picked.linkPath) ?? "")}
                    onChange={(e) => editLink(e.target.value)} />
                </div>
              )}
            </div>
          )}
        </aside>
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        title="删除这一项"
        message="删除后需点右上角「保存」才会真正生效，且不可恢复。确认删除？"
        confirmLabel="删除" danger
        onConfirm={() => { if (pendingDelete) commitListOp(pendingDelete.list, "delete", pendingDelete.index); setPendingDelete(null); }}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
